import { useCallback, useState } from 'react';
import { Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useCreateAccessLinkMutation } from '../../api/fileStorageApi';
import { BaseDialog } from '../common';
import { useDialogFormReset } from '../../hooks/useDialogFormReset';

type AccessLinkDialogProps = {
  open: boolean;
  fileId: string;
  onClose: () => void;
};

export const AccessLinkDialog = ({ open, fileId, onClose }: AccessLinkDialogProps) => {
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [description, setDescription] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [createAccessLink, { isLoading }] = useCreateAccessLinkMutation();
  const resetForm = useCallback(() => {
    setDateStart('');
    setDateEnd('');
    setDescription('');
    setGeneratedLink('');
    setCopied(false);
  }, []);
  const handleClose = useDialogFormReset(onClose, resetForm);

  const handleCreate = async () => {
    const result = await createAccessLink({
      fileKey: fileId,
      dateStart: dateStart || undefined,
      dateEnd: dateEnd || undefined,
      description: description || undefined,
    }).unwrap();
    const link = `${window.location.origin}/download?code=${result.code}`;
    setGeneratedLink(link);
  };

  const handleCopy = () => {
    void navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title="Create access link"
      maxWidth="sm"
      actions={
        <>
          <Button onClick={handleClose}>Close</Button>
          {!generatedLink && (
            <Button variant="contained" onClick={() => void handleCreate()} disabled={isLoading}>
              {isLoading ? 'Creating…' : 'Create link'}
            </Button>
          )}
        </>
      }
    >
      {!generatedLink ? (
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Date start"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
            />
            <TextField
              label="Date end"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
            />
          </Stack>
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Stack>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Access link created. Share this URL:
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={generatedLink}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton onClick={handleCopy} size="small" color={copied ? 'success' : 'default'}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Button size="small" onClick={resetForm}>
            Create another
          </Button>
        </Stack>
      )}
    </BaseDialog>
  );
};
